import { useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import { CircularProgress } from '@mui/material';
// ui
import { Button } from 'src/@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
// components
import Iconify from 'src/components/Iconify';
// utils
import { readJSONFile, readEnvelope } from 'src/utils/exportImport.utils';

// ----------------------------------------------------------------------

/**
 * Modal genérico para importar um arquivo .json exportado pela plataforma.
 *
 * Props:
 * - open, onClose
 * - expectedKind: 'lais-chatflow' | 'lais-funil' | 'lais-automacao'
 * - title, description
 * - onImport(data) => Promise<void | { warnings?: string[], message?: string }>
 *     recebe o `envelope.data` já validado. Pode retornar um resumo opcional
 *     com avisos de campos que precisam ser reconfigurados no cliente destino.
 */
export default function ModalImportarJSON({ open, onClose, expectedKind, onImport, title = 'Importar', description }) {
    const { enqueueSnackbar } = useSnackbar();
    const [importing, setImporting] = useState(false);
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;
        if (selectedFile.type !== 'application/json' && !selectedFile.name.endsWith('.json')) {
            enqueueSnackbar('Por favor, selecione um arquivo .json.', { variant: 'warning' });
            return;
        }
        setFile(selectedFile);
        setResult(null);
    };

    const handleImport = async () => {
        if (!file) return;
        setImporting(true);
        try {
            const envelope = await readJSONFile(file);
            const data = readEnvelope(envelope, expectedKind);
            const summary = (await onImport(data)) || {};
            setResult({ warnings: summary.warnings || [], message: summary.message });
            enqueueSnackbar('Importação concluída!', { variant: 'success' });
        } catch (error) {
            console.error(error);
            enqueueSnackbar(error?.message || 'Falha na importação.', { variant: 'error' });
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setResult(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md w-full bg-white border-none shadow-2xl rounded-2xl p-0 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <div className="p-2 bg-violet-100 rounded-lg">
                                <Iconify icon="solar:import-bold" className="w-5 h-5 text-[#7C3AED]" />
                            </div>
                            {title}
                        </DialogTitle>
                    </DialogHeader>
                </div>

                <div className="p-6 flex flex-col items-center gap-6">
                    {!result ? (
                        <>
                            <div
                                className={`w-full h-48 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors ${file ? 'border-[#7C3AED] bg-violet-50' : 'border-gray-300 hover:border-violet-500 hover:bg-violet-50'
                                    }`}
                                onClick={() => !importing && fileInputRef.current?.click()}
                            >
                                {importing ? (
                                    <CircularProgress />
                                ) : file ? (
                                    <>
                                        <Iconify icon="solar:file-check-bold" className="w-12 h-12 text-[#7C3AED] mb-2" />
                                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                        <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                                    </>
                                ) : (
                                    <>
                                        <Iconify icon="solar:upload-minimalistic-bold" className="w-12 h-12 text-gray-300 mb-2" />
                                        <p className="text-sm font-medium text-gray-600">Clique para selecionar o arquivo</p>
                                        <p className="text-xs text-gray-400 mt-1">.json</p>
                                    </>
                                )}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept=".json,application/json"
                                    onChange={handleFileChange}
                                    disabled={importing}
                                />
                            </div>

                            {description && (
                                <div className="w-full bg-gray-50 p-4 rounded-lg text-xs text-gray-500">
                                    {description}
                                </div>
                            )}

                            <div className="flex w-full gap-3 mt-2">
                                <Button variant="outline" onClick={handleClose} className="flex-1 rounded-xl" disabled={importing}>
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={handleImport}
                                    className="flex-1 rounded-xl bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                                    disabled={!file || importing}
                                >
                                    {importing ? 'Importando...' : 'Importar'}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="w-full flex flex-col items-center animate-in fade-in zoom-in duration-300">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                                <Iconify icon="solar:check-circle-bold" className="w-8 h-8" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">Importação Finalizada</h3>
                            {result.message && <p className="text-sm text-gray-500 text-center mb-2">{result.message}</p>}

                            {result.warnings?.length > 0 && (
                                <div className="w-full bg-amber-50 border border-amber-100 rounded-xl p-4 my-4">
                                    <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-2">
                                        <Iconify icon="solar:danger-triangle-bold" className="w-4 h-4" />
                                        Reconfigure no cliente atual
                                    </div>
                                    <p className="text-xs text-amber-600 mb-2">
                                        Os campos abaixo foram limpos por pertencerem a outro cliente. Abra o item e preencha-os novamente:
                                    </p>
                                    <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside max-h-40 overflow-y-auto">
                                        {result.warnings.map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <Button onClick={handleClose} className="w-full rounded-xl bg-gray-900 text-white hover:bg-gray-800 mt-2">
                                Concluir
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
